import { ArrowUpRight, Brain, Rocket, ShieldCheck } from 'lucide-react';

/**
 * Cinematic full-bleed showcase — three tall image cards with real photography.
 * Sits directly under the Hero, keeping the dark cinematic feel before the page
 * transitions to lighter sections.
 */
const CARDS = [
  {
    id: 'intelligence',
    kicker: 'AI Native',
    title: 'Intelligent by design.',
    body: 'Claude Sonnet 4.5, GPT-5, and Gemini 2.5 Pro on tap — auto-routed to the perfect model for every task in your build.',
    icon: Brain,
    image:
      'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1600&q=85&auto=format&fit=crop',
    accent: 'from-cyan-400 via-indigo-500 to-fuchsia-500',
    tone: 'cyan',
  },
  {
    id: 'ship',
    kicker: 'Global Deploy',
    title: 'Ship from anywhere.',
    body: 'One conversation. One click. Your app goes live on a global edge network with SSL, custom domain and CDN in under 90 seconds.',
    icon: Rocket,
    image:
      'https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=1600&q=85&auto=format&fit=crop',
    accent: 'from-amber-300 via-orange-400 to-rose-500',
    tone: 'amber',
  },
  {
    id: 'security',
    kicker: 'Enterprise Grade',
    title: 'Secure by default.',
    body: 'End-to-end encryption, SOC 2 aligned infrastructure, private networking and secrets management — baked into every project.',
    icon: ShieldCheck,
    image:
      'https://images.unsplash.com/photo-1614064641938-3bbee52942c7?w=1600&q=85&auto=format&fit=crop',
    accent: 'from-rose-500 via-pink-500 to-red-500',
    tone: 'red',
  },
];

export default function CinematicShowcase() {
  return (
    <section
      id="showcase"
      className="relative py-24 md:py-32 text-white overflow-hidden"
      style={{
        background:
          'radial-gradient(1200px 800px at 15% 10%, rgba(30,64,175,0.35) 0%, transparent 55%),' +
          'radial-gradient(1000px 700px at 85% 90%, rgba(147,51,234,0.25) 0%, transparent 60%),' +
          'linear-gradient(180deg, #030712 0%, #050d1f 50%, #030918 100%)',
      }}
    >
      {/* Fine grid overlay for cinematic depth */}
      <div className="absolute inset-0 hero-grid opacity-20 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        {/* Section header */}
        <div className="max-w-3xl mb-14 md:mb-20">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.4em] text-cyan-300/80 font-semibold uppercase mb-5">
            <span className="h-1 w-1 rounded-full bg-cyan-300" />
            The Platform
          </div>
          <h2 className="text-4xl md:text-6xl font-black tracking-tight leading-[1.02]">
            <span className="bg-clip-text text-transparent bg-gradient-to-br from-white via-white to-slate-400">
              Every capability an
            </span>
            <br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-300 via-indigo-400 to-fuchsia-500">
              AI dev studio needs.
            </span>
          </h2>
          <p className="mt-6 text-slate-300/90 text-lg max-w-2xl leading-relaxed">
            Not a chatbot. Not a code snippet. A full-blown production stack — assembled,
            deployed and secured by autonomous agents while you focus on the idea.
          </p>
        </div>

        {/* Cinematic image cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {CARDS.map((c) => {
            const Icon = c.icon;
            return (
              <article
                key={c.id}
                data-testid={`cinematic-card-${c.id}`}
                className="group relative rounded-3xl overflow-hidden border border-white/10 aspect-[4/5] cursor-pointer transition-all duration-500 hover:border-white/25 hover:-translate-y-1"
                style={{
                  boxShadow:
                    '0 30px 80px -30px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.05)',
                }}
              >
                {/* Image */}
                <img
                  src={c.image}
                  alt={c.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />

                {/* Colour wash to unify with our brand */}
                <div
                  className={`absolute inset-0 mix-blend-multiply opacity-70 bg-gradient-to-br ${c.accent}`}
                />

                {/* Bottom cinematic gradient for text legibility */}
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      'linear-gradient(180deg, rgba(3,7,18,0.15) 0%, rgba(3,7,18,0.35) 45%, rgba(3,7,18,0.92) 100%)',
                  }}
                />

                {/* Top-left icon chip */}
                <div className="absolute top-5 left-5 flex items-center gap-2">
                  <div className="h-9 w-9 rounded-xl bg-white/10 backdrop-blur-lg border border-white/20 flex items-center justify-center">
                    <Icon size={16} className="text-white" strokeWidth={2} />
                  </div>
                  <span className="text-[10px] font-bold tracking-[0.3em] uppercase text-white/90 px-2.5 py-1 rounded-full bg-white/10 backdrop-blur border border-white/15">
                    {c.kicker}
                  </span>
                </div>

                {/* Top-right arrow */}
                <div className="absolute top-5 right-5 h-9 w-9 rounded-xl bg-white/10 backdrop-blur-lg border border-white/20 flex items-center justify-center transition-transform duration-500 group-hover:rotate-45">
                  <ArrowUpRight size={16} className="text-white" strokeWidth={2.2} />
                </div>

                {/* Bottom text block */}
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-7">
                  <h3 className="text-2xl md:text-[28px] font-black tracking-tight leading-tight text-white">
                    {c.title}
                  </h3>
                  <p className="mt-2.5 text-[14px] text-white/85 leading-relaxed max-w-[95%]">
                    {c.body}
                  </p>

                  <div className="mt-5 flex items-center gap-2 text-[12px] font-semibold text-white/90 uppercase tracking-[0.2em]">
                    <span>Explore</span>
                    <span className="inline-block w-8 h-px bg-white/60 transition-all duration-500 group-hover:w-14" />
                  </div>
                </div>

                {/* Subtle top rim highlight */}
                <div className="absolute inset-x-0 top-0 h-24 pointer-events-none bg-gradient-to-b from-white/10 to-transparent opacity-60" />
              </article>
            );
          })}
        </div>

        {/* Small footer strip */}
        <div className="mt-14 flex flex-wrap items-center justify-between gap-4 text-sm">
          <div className="text-slate-400">
            Trusted by <span className="text-white font-semibold">10,000+</span> teams shipping in production.
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {['React', 'FastAPI', 'MongoDB', 'Stripe', 'Claude 4.5', 'GPT-5'].map((t) => (
              <span
                key={t}
                className="text-[11px] px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-slate-300 backdrop-blur"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Fade to page */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-black pointer-events-none" />
    </section>
  );
}
